<template>
  <content-subpage
    class="announcement-view-content-subpage"
    :visible="visible"
    :titles="titles"
    :content-title="t('announcement_details_title')"
    @content-subpage-hidden="actionHidden"
  >
    <template #main-content>
      <overlay-loader v-if="isLoading" />
      <template v-else>
        <div class="announcement-details">
          <div v-if="hasEditPermission" class="announcement-actions">
            <overflow-menu :flip-menu="true">
              <template #trigger>
                <svg-icon name="Kebab24" kind="action" />
              </template>
              <OverflowMenuItem @click="handleEdit">
                <svg-icon name="Pencil24" kind="action" />
                {{ t("button_edit") }}
              </OverflowMenuItem>
              <OverflowMenuItem @click="handleDelete">
                <svg-icon name="Trash24" kind="action" />
                {{ t("button_delete") }}
              </OverflowMenuItem>
            </overflow-menu>
          </div>
          <cv-form class="announcement-details-content">
            <div class="field-group sender-receiver">
              <dl
                v-if="hasStudentIndividualNotification || hasTeacherIndividualNotification"
                class="field-set type-text receiver"
              >
                <dt class="field-label">
                  {{ t("announcement_receiver_receiver_selection_label") }}
                </dt>
                <template v-if="hasStudentIndividualNotification">
                  <dd class="field-value">
                    <label> {{ t("announcement_receiver_student_label") }}</label>
                    <span>{{ notificationStudentIndividuals }}</span>
                  </dd>
                </template>
                <template v-if="hasTeacherIndividualNotification">
                  <dd class="field-value">
                    <label>{{ t("announcement_receiver_teacher_label") }}</label>
                    <span> {{ notificationTeacherIndividuals }}</span>
                  </dd>
                </template>
              </dl>
              <dl v-else-if="isClassGroupNotification" class="field-set type-text receiver">
                <dt class="field-label">
                  {{ t("announcement_receiver_receiver_selection_label") }}
                </dt>
                <dd class="field-value">
                  <span>{{ t("announcement_receiver_all_student_teachers_label") }}</span>
                </dd>
              </dl>
              <dl
                v-else-if="isSchoolNotification || isSystemNotification"
                class="field-set type-text receiver"
              >
                <dt class="field-label">
                  {{ t("announcement_receiver_receiver_selection_label") }}
                </dt>
                <dd class="field-value">{{ notificationReceiver.name }}</dd>
              </dl>
              <dl
                v-if="hasIndividualReceiver && !isClassGroupNotification"
                class="field-set type-text individual"
              >
                <dt class="field-label">{{ t("announcement_individual_receiver_form_label") }}</dt>
                <dd class="field-value">{{ notificationIndividualReceiver }}</dd>
              </dl>
              <dl class="field-set type-text owner">
                <dt class="field-label">{{ t("announcement_owner_form_label") }}</dt>
                <dd class="field-value">{{ createdBy }}</dd>
              </dl>
              <dl v-if="isClassGroupNotification" class="field-set type-text class-group">
                <dt class="field-label">
                  {{
                    isCommunity
                      ? t("announcement_receiver_community_selection_label")
                      : t("announcement_receiver_class_group_selection_label")
                  }}
                </dt>
                <dd class="field-value">
                  <label-component
                    kind="class-group"
                    :text="notificationReceiver.name"
                    :subject-key="notificationReceiver.subjectKey"
                  />
                </dd>
              </dl>
              <dl v-if="!isSystemNotification" class="field-set type-text school">
                <dt class="field-label">{{ t("announcement_receiver_school_form_label") }}</dt>
                <dd class="field-value">{{ schoolName }}</dd>
              </dl>
              <dl
                v-if="!isStudent && !isSystemNotification"
                class="field-set type-date last-modified-by"
              >
                <dt class="field-label">{{ t("announcement_last_modified_by_form_label") }}</dt>
                <dd class="field-value">{{ notification.modifiedByName }}</dd>
              </dl>
            </div>
            <div class="field-group content">
              <dl class="field-set type-text title">
                <dt class="field-label">
                  {{ t("announcement_title_form_label") }}
                </dt>
                <dd class="field-value">
                  <b>{{ notification.title }}</b>
                </dd>
              </dl>
              <dl class="field-set type-text message">
                <dd class="field-label">{{ t("message_form_label") }}</dd>
                <dd class="field-value">
                  <rich-text :content="notification.content" />
                </dd>
              </dl>
            </div>
            <div class="field-group datetime">
              <dl class="field-set type-date start-date">
                <dt class="field-label">{{ t("announcement_start_date_form_label") }}</dt>
                <dd class="field-value">{{ startDate }}</dd>
              </dl>
            </div>
          </cv-form>

          <!-- Link to Announcement Listing -->
          <cv-button
            class="button view-announcement"
            kind="tertiary"
            size="field"
            @click="routeToNotification"
          >
            {{ t("announcement_view_all_link_label") }}
          </cv-button>
        </div>
      </template>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm";
import {
  ContentSubpageParams,
  useContentSubpage,
} from "@plugins/content-subpage/composables/content-subpage.ts";
import DateTimeFormatter from "@utils/datetime.formatter";
import NotificationUtils from "@modules/notification/service/notification.utils";
import { isEmpty } from "@utils/var.check";
import { useStore } from "vuex";
import {
  NOTIFICATION_AUDIENCE,
  NOTIFICATION_SCOPE,
  NAMESPACE,
} from "../service/notification.constant";

// Props
interface NotificationViewContentSubpageParams extends ContentSubpageParams {
  //TODO: check again for this prop
  kind?: string;
  data?: {
    notification?: Record<string, null>;
    scope?: string;
    classGroup?: { publicDomain: boolean };
    [key: string]: unknown;
  };
}
interface Props {
  params?: NotificationViewContentSubpageParams;
}
const props = withDefaults(defineProps<Props>(), {
  params: null,
});

// Composables
const { t } = useI18n();
const { vm, vmExpose } = useVm();

const {
  titles,
  visible,
  actionHidden,
  isComponentDirty,
  removeComponentDirty,
  setVisible,
  actionHiddenCallBack,
  defaultExpose,
} = useContentSubpage(props.params);

// Store getters/setters
const store = useStore();
const isStudent = computed(() => store.getters[`${NAMESPACE.AUTH}/isStudent`]);

// Emits
const emit = defineEmits<{
  (e: "showEdit", payload: { uuid: string; scope: string; classGroup?: unknown }): void;
  (e: "delete", uuid: string, component: unknown): void;
  (e: "routeToNotification", notification: unknown): void;
}>();

// Data
const isLoading = ref(false);
const notification = ref(props.params?.data?.notification ?? {});

// Computed
const notificationReceiver = computed(() => {
  const receivers: string[] = [];
  const recipients = notification.value.notificationRecipients ?? []; // Safe access

  if (props.params?.data?.classGroup) {
    return {
      name: props.params.data.classGroup.groupName,
      subjectKey: props.params.data.classGroup.subjectKey,
    };
  }
  for (const recipient of recipients) {
    const audienceType = recipient.audienceType;

    if (audienceType === NOTIFICATION_AUDIENCE.INDIVIDUAL) {
      // Skip for group receivers
    } else if (audienceType === NOTIFICATION_AUDIENCE.ALL) {
      return { name: t("announcement_receiver_all_label") };
    } else if (
      audienceType === NOTIFICATION_AUDIENCE.ALL_STUDENTS &&
      isEmpty(recipient.studentGroupCollectionId)
    ) {
      return { name: t("announcement_receiver_all_students_label") };
    } else if (audienceType === NOTIFICATION_AUDIENCE.ALL_TEACHERS) {
      return { name: t("announcement_receiver_all_teachers_label") };
    } else if (audienceType === NOTIFICATION_AUDIENCE.FORM_CLASS) {
      // Collect formClassCode
      receivers.push(recipient.formClassCode);
    } else {
      const recipientDetails = recipient.groupName?.split(" ") ?? [];
      const [name, subjectKey] = recipientDetails;
      return {
        name,
        subjectKey,
      };
    }
  }
  const name = isEmpty(receivers) ? "" : receivers.join(", ");
  return { name };
});

const notificationIndividualReceiver = computed(() =>
  NotificationUtils.getNotificationIndividualReceiver(notification.value),
);
const notificationTeacherIndividuals = computed(() =>
  NotificationUtils.getNotificationTeacherIndividuals(notification.value),
);
const notificationStudentIndividuals = computed(() =>
  NotificationUtils.getNotificationStudentIndividuals(notification.value),
);
const hasIndividualReceiver = computed(() => !isEmpty(notificationIndividualReceiver.value));
const hasEditPermission = computed(
  () => !isEmpty(notification.value.hasEditPermission) && notification.value.hasEditPermission,
);
const createdBy = computed(() => NotificationUtils.getNotificationCreatedBy(notification.value));
const schoolName = computed(() => notification.value.notificationRecipients?.[0]?.schoolName ?? "");
const startDate = computed(() =>
  DateTimeFormatter.formatLocalDateTimeToDateTimeAmPmWithComma(notification.value.startDate),
);
const isCommunity = computed(() => props.params?.data?.classGroup?.publicDomain ?? false);
const isClassGroupNotification = computed(
  () => props.params?.data?.scope === NOTIFICATION_SCOPE.GROUP,
);
const isSystemNotification = computed(
  () => props.params?.data?.scope === NOTIFICATION_SCOPE.SYSTEM,
);
const isSchoolNotification = computed(
  () => props.params?.data?.scope === NOTIFICATION_SCOPE.SCHOOL,
);
const hasTeacherIndividualNotification = computed(
  () => isClassGroupNotification.value && !isEmpty(notificationTeacherIndividuals.value),
);
const hasStudentIndividualNotification = computed(
  () => isClassGroupNotification.value && !isEmpty(notificationStudentIndividuals.value),
);

// Methods
function handleEdit() {
  // Switch to edit mode in parent subpage
  actionHidden(); // Assumption: from mixin via vm
  emit("showEdit", {
    uuid: notification.value.uuid,
    scope: props.params?.data?.scope,
    classGroup: props.params?.data?.classGroup,
  });
}

function handleDelete() {
  // Send delete event in listing
  emit("delete", notification.value.uuid, vm); // 'this' -> vm
}

function routeToNotification() {
  actionHidden(); // Assumption: from mixin
  emit("routeToNotification", notification.value);
}

// Lifecycle
onMounted(() => {
  titles.value.push(notification.value.title);
});

defineExpose({
  titles,
  isComponentDirty,
  removeComponentDirty,
  setVisible,
  actionHiddenCallBack,
  params: props.params,
  ...defaultExpose,
});

vmExpose({
  actionHidden,
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.announcement-view-content-subpage {
  .main {
    position: relative;
  }

  .announcement-details {
    display: flex;
    flex-direction: column;

    .announcement-actions {
      position: absolute;
      right: 1rem;
      top: 0.5rem;
    }
  }
  .announcement-details-content {
    margin-top: 0;
    .field-group {
      &.sender-receiver {
        @include media("desktop") {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          column-gap: $spacing-05;
        }

        .receiver {
          .field-value {
            display: flex;
            flex-flow: column;

            &:not(:last-child) {
              margin-bottom: $spacing-05;
            }

            label {
              @include carbon--type-style("productive-heading-01");
              color: $text-02;
              margin-bottom: $spacing-03;
            }
          }
        }
      }
      &.content {
        margin-top: $spacing-05;
      }
      &.datetime {
        @include media("desktop") {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-gap: $spacing-05;
        }

        margin-top: $spacing-05;
        padding-top: $spacing-05;
        border-top: 1px solid $ui-03;
      }

      .field-set {
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .view-announcement {
    margin: $spacing-05 auto;
  }
}
</style>
